import { useState, Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'

export default function UploadModal({ open, setOpen, setHistory }) {
    const cancelButtonRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [files, setFiles] = useState([]);

    const cancel = () => {
        setUploading(false);
        setOpen(false);
        setError(null);
        setFiles([]);
    }

    const upload = () => {
        setHistory(files);
        cancel();
    }

    const handleChange = e => {
        try {
            if (uploading) return;
            setUploading(true);
            for (const file of e.target.files) {
                if (file.name && !file.name.includes("StreamingHistory"))
                    continue;
                const fileReader = new FileReader();
                fileReader.readAsText(file, "UTF-8");
                fileReader.onload = e => {
                    setFiles(files => [...files, ...JSON.parse(e.target.result)]);
                };
            }
        } catch (e) {
            console.error(e);
            setError("File could not be parsed. Is the format correct?");
        }
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={cancel}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ArrowUpTrayIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Upload history files
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Select the files from your Spotify download to upload. Only Streaming History will be processed.
                                                </p>
                                                {!uploading && <label htmlFor="input" className="text-sm font-semibold text-blue-500 pt-4">Click to upload</label>}
                                                {uploading && !error && <p className="text-indigo-500 font-medium text-sm pt-4">Files successfully processed</p>}
                                                {uploading && error && <p className="text-red-500 font-medium text-sm pt-4">{error}</p>}
                                                <input id="input" multiple type="file" className="hidden" onChange={handleChange} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className="inline-flex w-full disabled:bg-gray-200 disabled:pointer-none justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                        disabled={!uploading}
                                        onClick={upload}
                                    >
                                        Confirm Upload
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={cancel}
                                        ref={cancelButtonRef}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}